import { useEffect, useState } from 'react';

import type { NewsletterSummary } from '../api/types';

import { fetchNewsletters } from '../api/infoHubApi';

export type NewsletterArchiveState = 'loading' | 'ready' | 'error';

export interface UseNewsletterArchiveResult {
  items: NewsletterSummary[];
  total: number;
  state: NewsletterArchiveState;
}

// The newsletter archive is low-volume and grouped by year in the UI, so the whole active set
// is fetched in one call and grouped client-side rather than paged.
const ARCHIVE_PAGE_SIZE = 500;

/**
 * Loads the active newsletter archive (newest first). `search` filters on the title
 * server-side. `notfound` isn't a state here — an empty archive is a valid `ready` result.
 *
 * `limit` caps how many come back, matching `useHubArchive`. The archive page wants the
 * lot; a caller that only needs `total` — the landing's count — asks for one and reads the
 * server-side figure off it rather than dragging the whole set across for a number.
 */
export const useNewsletterArchive = (
  search?: string,
  limit: number = ARCHIVE_PAGE_SIZE,
): UseNewsletterArchiveResult => {
  const [items, setItems] = useState<NewsletterSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<NewsletterArchiveState>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    const load = async (): Promise<void> => {
      try {
        const result = await fetchNewsletters({
          search: search?.trim() || undefined,
          page: 1,
          pageSize: limit,
        });
        if (cancelled) {
          return;
        }
        setItems(result.items);
        setTotal(result.total);
        setState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error loading newsletter archive:', error);
        setState('error');
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [search, limit]);

  return { items, total, state };
};
